import type { Topic as TopicType } from '@/@types/topic'
import Topic from '@/components/Topic'
import { SEARCH_TOPIC } from '@/constants/api'
import { getTopicSuggestions } from '@/utils/fetchers/topic'
import { post } from '@/utils/request'
import { css } from '@emotion/react'
import { useQuery } from '@tanstack/react-query'
import { isEmpty } from 'lodash-es'
import { AnimatePresence, motion } from 'motion/react'
import { forwardRef, useCallback, useState } from 'react'
import { type ControllerRenderProps, useWatch } from 'react-hook-form'
import TopicSuggestions from './TopicSuggestions'
import type { PostFormValue } from './types'

interface TopicSearchResult {
	count: number
	data: TopicType[]
	highlightWord: {
		singleMaxHighlightTime: number
		totalMaxHighlightTime: number
		words: string[]
	}
	loadMoreKey?: any
}

export interface TopicPickerProps extends ControllerRenderProps<PostFormValue, 'topic'> {}

export default forwardRef<HTMLDivElement, TopicPickerProps>(function TopicPicker(
	{ onChange, value, ...props },
	ref
) {
	const content = useWatch<PostFormValue, 'content'>({
		name: 'content',
	})
	const attachments = useWatch<PostFormValue, 'attachments'>({
		name: 'attachments',
	})
	const isTopicPickerShow = !isEmpty(content) || !isEmpty(attachments)

	// suggestions
	const [suggestions, setSuggestions] = useState<TopicType[]>([])
	const handleTopicSearch = useCallback((keyword: string) => {
		post<TopicSearchResult>(SEARCH_TOPIC, {
			keywords: keyword,
			limit: 20,
			onlyUserPostEnabled: true,
			type: 'ALL',
		})
			.then((res) => res.data.data)
			.then((topics) => {
				setSuggestions(topics)
			})
	}, [])

	const isSuggestionsShow = isEmpty(value)

	useQuery({
		queryKey: ['SUGGEST', content, JSON.stringify(attachments)],
		queryFn: () =>
			getTopicSuggestions(content, attachments).then((data) => {
				setSuggestions(data)
				return data
			}),
		enabled: isTopicPickerShow,
	})

	return (
		<motion.div
			initial={{
				opacity: 0,
			}}
			animate={{
				opacity: isTopicPickerShow ? 1 : 0,
			}}
			exit={{
				opacity: 0,
			}}
			css={css`
				display: grid;
				align-items: center;
				grid-template-columns: auto 1fr;
				overflow: hidden;
			`}
			{...props}
			ref={ref}
		>
			<Topic
				data={value}
				cleanable
				onClean={() => {
					onChange?.(undefined)
				}}
				onSearch={handleTopicSearch}
			/>

			<AnimatePresence>
				{isSuggestionsShow && (
					<TopicSuggestions
						initial={{
							opacity: 0,
						}}
						animate={{
							opacity: 1,
						}}
						exit={{
							opacity: 0,
						}}
						onSelect={onChange}
						data={suggestions}
					/>
				)}
			</AnimatePresence>
		</motion.div>
	)
})
